import React, { useState, useEffect } from 'react';
import { IoChevronBackSharp, IoChevronForwardSharp } from "react-icons/io5"
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { updateApplication, updateApplicationData, safePdf } from '../../../../store/reducers/applicationSlice';
import { showSuccess, showError } from '../../../../helpers/toast';
import { createNotification } from "../../../../store/reducers/notificationSlice";
import { prepareHtmlForPdf } from '../../../../utility/utility';


const FormHeader = (props) => {
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadingIds, setDownloadingIds] = useState([]);
    const [inputFields, setInputFields] = useState({});
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const uploadedFiles = props.uploadedFiles || [];
    const user = useSelector((state) => state.auth.user);
    const authToken = user.access_token;
    const application = useSelector((state) => state.applications.application);
    const pageIds = props.pageIds || [];

    useEffect(() => {
        setInputFields(application?.json_data || {});
    }, [application]);


    const handleGoPrev = (event) => {
        //change page        
        props.previousPage(event);
    }

    const handleGoForw = (event) => {
        //change page
        props.nextPage(event);
    }

    const handleDownload = async (item) => {
        const applicationId = item.applicationId || props.applicationId;
        const userId = item.userId || props.userId || user.user?.id;
        if (!applicationId) {
            showError('Application ID is missing');
            return;
        }
        if (!userId) {
            showError('User ID is missing');
            return;
        }

        // Prevent multiple clicks
        if (isDownloading || downloadingIds.includes(applicationId)) {
            return;
        }

        setDownloadingIds((prev) => [...prev, applicationId]);
        setIsDownloading(true);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}file/${userId}/downloadapplicationfiles/${applicationId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/zip",
                        Authorization: `Bearer ${authToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status} ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `application_${applicationId}_files.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            showSuccess('Files downloaded successfully!');

        } catch (error) {
            showError(`Download failed: ${error.message}`);
        } finally {
            setDownloadingIds((prev) => prev.filter((id) => id !== applicationId));
            setIsDownloading(false);
        }
    };

    const handleSaveApplication = async () => {

        let htmlForm = document.getElementById("DocumentPDF").outerHTML;

        try {
            // Call saveChanges function if provided (for file updates)
            if (props.saveChanges) {
                console.log('Calling saveChanges for file updates');
                await props.saveChanges();
            }

            const response = await dispatch(updateApplicationData({
                id: application.id,
                token: authToken,
                userId: application.user_id,
                json_data: props?.json_data,
                state: application.state,
            }));

            if (response.payload) {
                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds),
                    user: { id: application.user_id },
                    id: params.id,
                    token: authToken,
                }));
            } else {
                showError('Failed to save application. Please try again.');
            }
        } catch (error) {
            showError('Error saving application. Please try again.');
        }
    };

    const handleSubmitToGB = async (item) => {

        // alert('submit to GB form header');

        let htmlForm = document.getElementById("DocumentPDF").outerHTML;

        const currentAppState = application?.state;
        const newState = currentAppState === "descrepency_GB_Company" ? "descrepency_GB_GB" : "in_process";

        try {
            if (props.saveChanges) {
                console.log('Calling saveChanges for file updates');
                await props.saveChanges();
            }
            const response = await dispatch(updateApplicationData({
                id: item.applicationId || props.applicationId,
                form: props.applicationName,  // 
                state: newState,
                userId: user.user.id,
                token: authToken,
                json_data: props?.json_data, // Use props.json_data instead of empty object
            }));

            dispatch(createNotification({
                user_id: application?.user_id || application?.userId || item.userId || props.userId,
                applicationId: item.applicationId || props.applicationId,
                applicationName: props.applicationName,
                status: newState,
                accessToken: authToken,
                sendToGamingBoardAdmins: true,
                companyName: user?.user?.company_name,
                employeeName: application?.userName || application?.name // Fallback to app owner name if available
            }));
            showSuccess('Application submitted to Gaming Board successfully!');
            navigate(
                newState === "descrepency_GB_GB" ? "/discrepancies-gb" : "/in-process-applications"
            );
            // Refresh the applications list
            if (props.refreshApplications) {
                props.refreshApplications();
            }
            if (response.payload) {
                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds),
                    user: { id: application.user_id },
                    id: params.id,
                    token: authToken,
                }));
            } else {
                showError('Failed to save application. Please try again.');
            }
        } catch (error) {
            showError('Failed to submit application to Gaming Board');
        }
    };


    const handleAssignToEmployee = async (item) => {
        try {
            const newState = application.state === "descrepency_GB_Company" ? "descrepency_GB_Employee" : "dicrepancy_application_assigned_to_comapany_user";

            await dispatch(updateApplicationData({
                id: item.applicationId || props.applicationId,
                form: props.applicationName,
                state: newState,
                userId: user.user.id,
                token: authToken,
                json_data: props?.json_data
            }));

            dispatch(createNotification({
                user_id: application?.user_id || application?.userId || item.userId || props.userId,
                applicationId: item.applicationId || props.applicationId,
                applicationName: props.applicationName,
                status: newState,
                accessToken: authToken,
                sendToCompanyAdmins: true,
                sendToCompanyUser: true,
                companyName: user?.user?.company_name,
                employeeName: application?.userName || application?.name
            }));

            navigate('/discrepancies-company');
        }
        catch (error) {
            showError('Failed to assign application to employee');
        }
    };

    const handleSaveDraft = async (item) => {
        try {
            await dispatch(updateApplicationData({
                id: item.applicationId || props.applicationId,
                form: props.applicationName,
                state: "drafts",
                userId: user.user.id,
                token: authToken,
                json_data: props?.json_data,
            }));
            navigate('/draft');
        }
        catch (error) {
            showError('Failed to save application to drafts');
        }
    };


    const handleAssignToDiscrepancies = async (item) => {
        let htmlForm = document.getElementById("DocumentPDF").outerHTML;

        try {
            const response = await dispatch(updateApplicationData({
                id: item.applicationId || props.applicationId,
                form: props.applicationName,  // ← ADD THIS - was missing
                userId: user.user.id,
                state: "application_sent_to_discrepancy_by_company",
                token: authToken,
                json_data: props?.json_data      // ← FIXED - use inputFields instead of empty object
            }));

            dispatch(createNotification({
                user_id: application?.user_id || application?.userId || item.userId || props.userId,
                applicationId: item.applicationId || props.applicationId,
                applicationName: props.applicationName,
                status: "application_sent_to_discrepancy_by_company",
                accessToken: authToken,
                sendToCompanyAdmins: true,
                companyName: user?.user?.company_name,
                employeeName: application?.userName || application?.name
            }));

            showSuccess('Application assigned to employee successfully!');
            // Navigate to company discrepancy view
            const navId = item.applicationId || props.applicationId;
            if (navId) {
                navigate(`/view-discrepancies/company/${navId}`);
            }
            // Refresh the applications list
            if (props.refreshApplications) {
                props.refreshApplications();
            }
            if (response.payload) {
                dispatch(safePdf({
                    htmlForm: prepareHtmlForPdf(htmlForm, props?.json_data || {}, pageIds),
                    user: { id: application.user_id },
                    id: params.id,
                    token: authToken,
                }));
            } else {
                showError('Failed to save application. Please try again.');
            }
        } catch (error) {
            showError('Failed to assign application to employee');
        }
    };

    // const handleSubmitWithLoading = async (item) => {
    //     let htmlForm = document.getElementById("DocumentPDF").outerHTML;
    //     htmlForm = removeDisplayNoneByIds(htmlForm, pageIds);

    //     // Determine the new status based on current application status and user role
    //     let newStatus = "discrepancies"; // default status

    //     // Check multiple possible conditions for status change
    //     const shouldChangeStatusToAssigned = (
    //         (application?.state === "application_sent_to_discrepancy_by_company" || application?.state === "discrepancies") && user?.user?.user_role === "company_admin"
    //     );

    //     const shouldChangeStatusToCompany = (
    //         application?.state === "dicrepancy_application_assigned_to_comapany_user" &&
    //         user?.user?.user_role === "employee"
    //     );
    //     const shouldChangeStatusToGBCompany = (
    //         application?.state === "descrepency_GB_Employee" &&
    //         user?.user?.user_role === "employee"
    //     );


    //     if (shouldChangeStatusToAssigned) {
    //         newStatus = "dicrepancy_application_assigned_to_comapany_user";
    //     } else if (shouldChangeStatusToCompany) {
    //         newStatus = "application_sent_to_discrepancy_by_company";
    //     } else if (shouldChangeStatusToGBCompany) {
    //         newStatus = "descrepency_GB_Company";
    //     } else {
    //         showError('Status NOT changed - conditions not met');
    //     }
    //     // Update application data with the determined status
    //     await dispatch(updateApplicationData({

    //         id: item.applicationId || props.applicationId,
    //         form: props.applicationName,
    //         state: newStatus,
    //         userId: user.user.id,
    //         token: authToken,
    //         json_data: props?.json_data
    //     }));

    //     // Handle different status changes and navigation
    //     if (newStatus === "dicrepancy_application_assigned_to_comapany_user") {
    //         // Additional status update for the discrepancy assignment
    //         await dispatch(updateApplication({
    //             token: authToken,
    //             id: item.applicationId || props.applicationId,
    //             state: newStatus,
    //             currentTime: new Date().toLocaleTimeString(),
    //             statusMessage: "Discrepancy company assigned to employee"
    //         }));
    //         navigate('/discrepancies-company');
    //     } else if (newStatus === "application_sent_to_discrepancy_by_company") {
    //         // Additional status update for sending back to company
    //         await dispatch(updateApplication({
    //             token: authToken,
    //             id: item.applicationId || props.applicationId,
    //             state: newStatus,
    //             currentTime: new Date().toLocaleTimeString(),
    //             // statusMessage: "Discrepancy sent back to company",
    //             json_data: props?.json_data
    //         }));
    //         navigate('/discrepancies-company');
    //     } else {
    //         // Default navigation for other cases
    //         navigate('/in-process-applications');
    //     }

    //     dispatch(safePdf({
    //         htmlForm,
    //         user: { id: application.user_id },
    //         id: params.id,
    //         token: authToken,
    //     }));


    // };

    return (
        <>
            <div className='AppFormBar noShadow'>
                <div className='title'>
                    <div style={{
                        // width: "400px",
                        background: "#323639",
                        border: "solid 0px #ccc",
                        borderRadius: "5px",
                        padding: "5px",
                        fontSize: "14px",
                        lineHeight: "22px",
                        color: "#fff",
                    }}>
                        {props.applicationName}
                    </div>
                </div>



                <div className='pagination'>
                    <div className='pageNum d-flex align-items-center'>
                        <IoChevronBackSharp onClick={handleGoPrev} />

                        {/* Replace span with an input */}
                        <input
                            type="number"
                            value={props.currentPage || 1}
                            onChange={props.handlePageChange ? (e) => props.handlePageChange(Number(e.target.value)) : undefined}
                            readOnly={!props.handlePageChange}
                            style={{
                                width: 'max-content',
                                padding: '0',
                                background: '#191B1C',
                                color: 'white',
                                borderRadius: '5px',
                                textAlign: 'center',
                                WebkitAppearance: 'none',
                                border: 'none',
                            }}
                            min="1"
                            max={Number(props.totalPages)}
                        />

                        <span className='slash'>{`/`}<span style={{ marginLeft: 10 }}></span></span>{props.totalPages}
                        <IoChevronForwardSharp onClick={handleGoForw} />
                    </div>
                    {!props.displayInReview && (
                        <div className='d-flex gap-2'>
                            <button className='btn draft' type='submit' onClick={(() => props.saveToDrafts("drafts"))}>Save to Draft</button>
                            <button className='btn' type='submit' onClick={(props.handleSubmitWithLoading)}>Submit </button>
                        </div>
                    )}
                    {props.displayInReview && (
                        <div className="boardActions">
                            <div className="dropdown d-inline-flex">

                                <button
                                    className="btn dropdown-toggle "
                                    type="button"
                                    id="dropdownMenuButton"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                >
                                    View Actions
                                </button>

                                <ul
                                    className="dropdown-menu p-2"
                                    aria-labelledby="dropdownMenuButton"
                                >
                                    <li>
                                        <button
                                            onClick={() => {
                                                if (!isDownloading && !downloadingIds.includes(props.applicationId || application?.id)) {
                                                    handleDownload(props);
                                                }
                                            }}
                                            className="dropdown-item"
                                            style={{
                                                opacity: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 0.6 : 1,
                                                pointerEvents: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'none' : 'auto',
                                                cursor: isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'not-allowed' : 'pointer',
                                                background: 'none',
                                                border: 'none',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 16px'
                                            }}
                                            disabled={isDownloading || downloadingIds.includes(props.applicationId || application?.id)}
                                        >
                                            {isDownloading || downloadingIds.includes(props.applicationId || application?.id) ? 'Downloading...' : 'Download'}
                                        </button>
                                    </li>
                                    {props.applicationState === "application_submitted_by_company_user_to_company" && (
                                        <>
                                            <li>
                                                <Link className="dropdown-item" onClick={() => handleSubmitToGB(props)}>
                                                    Submit to GB
                                                </Link>
                                            </li>
                                            <li>
                                                <button
                                                    onClick={handleAssignToDiscrepancies}
                                                    className="dropdown-item"
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        width: '100%',
                                                        textAlign: 'left',
                                                        padding: '8px 16px'
                                                    }}
                                                >
                                                    Add to Discrepancies
                                                </button>
                                            </li>
                                        </>
                                    )}
                                    <li>
                                        <button
                                            onClick={handleSaveApplication}
                                            className="dropdown-item"
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                width: '100%',
                                                textAlign: 'left',
                                                padding: '8px 16px'
                                            }}
                                        >
                                            Save Application
                                        </button>
                                    </li>
                                    {props.userRole === "company_admin" && (
                                        <>
                                            <li>
                                                <Link className="dropdown-item" onClick={() => handleSubmitToGB(props)}>
                                                    Submit to GB
                                                </Link>
                                            </li>
                                            <li>
                                                <Link onClick={() => handleAssignToEmployee(props)} className="dropdown-item">
                                                    Assign to Employee
                                                </Link>
                                            </li>
                                        </>
                                    )}

                                    {/* {!props.status === "dicrepancy_application_assigned_to_comapany_user" && (
                                        <li>
                                            <Link onClick={() => handleAssignToDiscrepancies(props)} className="dropdown-item">
                                                Add to Discrepancies
                                            </Link>
                                        </li>
                                    )} */}

                                    {props.userRole === "employee" && props.applicationState === "application_submitted_by_company_user_to_company" && (
                                        <li>
                                            <Link onClick={() => handleSaveDraft(props)} className="dropdown-item">
                                                Save as Draft
                                            </Link>
                                        </li>
                                    )}
                                    {props.userRole === "employee" && (
                                        <li>
                                            <Link onClick={() => props.handleSubmitWithLoading && props.handleSubmitWithLoading()} className="dropdown-item">
                                                Assign to Company
                                            </Link>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div >
        </>
    )
}

export default FormHeader;